import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LoginForm } from '../components/LoginForm';
import { useReduxLoginForm } from '../hooks/useReduxLoginForm';
import { useAuthCheck } from '../hooks/useAuthCheck';
import { clearAuthChallenge } from '../../../redux/actions/authActions';
import { CHALLENGE_NAME } from '../../../utils/constants';

/**
 * LoginPage Component
 * 
 * Handles user authentication through a login form using Redux Saga.
 * Uses AWS Cognito for authentication.
 * Includes OTP verification flow before login.
 * Automatically redirects to appropriate page if user is already authenticated.
 * Handles NEW_PASSWORD_REQUIRED challenge by redirecting to reset password page.
 */
const LoginPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Check if user is already authenticated and redirect if needed
  const { isAuthenticated, isLoading: authCheckLoading } = useAuthCheck();
  
  // Get form state and handlers
  const { formData, isLoading, error, handleInputChange, handleSubmit, otpSent, otp, setOtp, otpVerified, setOtpVerified, handleSendOtp, handleVerifyOtp, isVerifying, timer, canResend, handleResendOtp } = useReduxLoginForm();

  // Get auth challenge from Redux state
  const authChallenge = useSelector((state: any) => state.auth.authChallenge);
  const challengeUser = useSelector((state: any) => state.auth.challengeUser);
  const userPermissions = useSelector((state: any) => state.userManagement.userPermissions);
  

  // Handle auth challenge redirect
  useEffect(() => {
    if (authChallenge !== '') {
      if (authChallenge === CHALLENGE_NAME) {
        navigate('/reset-password', {
          state: {
            email: challengeUser ? challengeUser.username : '',
            isRedirectedFromLogin: true,
          }
        });
        // Clear the challenge after redirecting
        dispatch(clearAuthChallenge());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChallenge]);

  // Show loading only while checking authentication (not after successful login)
  if (authCheckLoading && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EB1C37] mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show loading only while checking authentication (not after successful login)
  if (isAuthenticated && userPermissions.data === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EB1C37] mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking Permissions...</p>
        </div>
      </div>
    );
  }

  // Don't render login form if user is already authenticated
  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EB1C37] mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h2 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-[#EB1C37] to-rose-500 bg-clip-text text-transparent">
          <span className="text-[#EB1C37]">DATA</span> {' '}
          <span className="text-gray-500">360</span>
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to your account
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
            <LoginForm
              formData={formData}
              isLoading={isLoading}
              error={error}
              onInputChange={handleInputChange}
              onSubmit={handleSubmit}
              otpSent={otpSent}
              otp={otp}
              setOtp={setOtp}
              otpVerified={otpVerified}
              isVerifying={isVerifying}
              timer={timer}
              canResend={canResend}
              onResendOtp={handleResendOtp}
              onVerifyOtp={handleVerifyOtp}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 